<template>
  <v-container fluid>
    <v-card class="elevation-2 rounded-lg overflow-hidden">
      <!-- En-tête avec dégradé -->
      <div class="audit-header px-4 py-3">
        <div class="d-flex align-center">
          <v-icon color="white" class="mr-2">mdi-shield-check</v-icon>
          <h2 class="text-h5 font-weight-bold white--text mb-0">Audit du Système</h2>
          <v-spacer></v-spacer>
          <v-btn
              color="white"
              small
              text
              @click="refreshData"
              :loading="refreshing"
              :disabled="loading"
              class="mr-2"
          >
            <v-icon left size="18">mdi-refresh</v-icon>
            Actualiser
          </v-btn>
        </div>
      </div>

      <!-- Indicateur de chargement -->
      <v-progress-linear
          :active="loading"
          :indeterminate="loading"
          color="primary"
          height="3"
      ></v-progress-linear>

      <!-- Outils de filtrage et actions -->
      <v-card-text class="py-2 px-4">
        <v-row align="center">
          <v-col cols="12" sm="6" md="4">
            <v-text-field
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                label="Rechercher..."
                clearable
                hide-details
                outlined
                dense
                class="rounded-lg"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-menu
                ref="dateMenu"
                v-model="dateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                    v-model="dateRangeText"
                    label="Période"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"
                    hide-details
                    class="rounded-lg"
                ></v-text-field>
              </template>
              <v-date-picker
                  v-model="dates"
                  range
                  no-title
                  color="primary"
                  @input="dateMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-select
                v-model="methodFilter"
                :items="methodOptions"
                label="Méthode"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-call-split"
                clearable
                class="rounded-lg"
            ></v-select>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="12" sm="6" md="2" class="text-right">
            <v-btn
                color="primary"
                class="elevation-1"
                :disabled="!serveraudits.length"
                @click="downloadCSV"
            >
              <v-icon left>mdi-download</v-icon>
              Exporter CSV
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- Tableau des audits -->
      <v-data-table
          :headers="headers"
          :items="filteredAudits"
          :search="search"
          :loading="loading"
          :items-per-page.sync="itemsPerPage"
          :items-per-page-options="[10, 15, 25, 50]"
          :single-expand="singleExpand"
          :expanded.sync="expanded"
          :footer-props="{
          'items-per-page-text': 'Lignes par page',
          'items-per-page-options': [10, 15, 25, 50],
          'show-first-last-page': true,
          'show-current-page': true,
          'page-text': '{0}-{1} sur {2}'
        }"
          show-expand
          @click:row="expandRow"
          class="audit-table elevation-0"
      >
        <!-- Formatage des dates -->
        <template v-slot:[`item.dateStart`]="{ item }">
          <div class="d-flex flex-column">
            <span class="text-body-2 font-weight-medium">{{ formatDateOnly(item.dateStart) }}</span>
            <span class="text-caption grey--text">{{ formatTimeOnly(item.dateStart) }}</span>
          </div>
        </template>

        <template v-slot:[`item.dateEnd`]="{ item }">
          <div class="d-flex flex-column">
            <span class="text-body-2 font-weight-medium">{{ formatDateOnly(item.dateEnd) }}</span>
            <span class="text-caption grey--text">{{ formatTimeOnly(item.dateEnd) }}</span>
          </div>
        </template>

        <!-- Méthode de la requête -->
        <template v-slot:[`item.method`]="{ item }">
          <v-chip
              x-small
              :color="getMethodColor(item.method)"
              dark
              label
          >
            {{ item.method }}
          </v-chip>
        </template>

        <!-- URL formatée -->
        <template v-slot:[`item.requestURI`]="{ item }">
          <div class="text-truncate" style="max-width: 200px;" :title="item.requestURI">
            {{ item.requestURI }}
          </div>
        </template>

        <!-- Utilisateur distant avec icône -->
        <template v-slot:[`item.remoteUser`]="{ item }">
          <div class="d-flex align-center">
            <v-icon
                small
                color="primary"
                class="mr-1"
                v-if="item.remoteUser"
            >
              mdi-account
            </v-icon>
            <span>{{ item.remoteUser || '-' }}</span>
          </div>
        </template>

        <!-- Adresse IP distante -->
        <template v-slot:[`item.remoteAddress`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <div class="d-flex align-center" v-bind="attrs" v-on="on">
                <v-icon
                    x-small
                    color="grey"
                    class="mr-1"
                >
                  mdi-ip
                </v-icon>
                <span>{{ item.remoteAddress }}</span>
              </div>
            </template>
            <span>{{ item.remoteAddress }}</span>
          </v-tooltip>
        </template>

        <!-- Détails étendus -->
        <template v-slot:[`expanded-item`]="{ headers, item }">
          <td :colspan="headers.length" class="pa-4">
            <v-card flat class="expanded-details pa-3 rounded-lg grey lighten-5">
              <div class="d-flex mb-2">
                <v-chip color="primary" label small class="mr-2">Détails complets</v-chip>
                <v-spacer></v-spacer>
                <v-btn x-small text color="grey" @click="copyToClipboard(item)">
                  <v-icon small left>mdi-content-copy</v-icon>
                  Copier
                </v-btn>
              </div>
              <vue-json-pretty
                  :data="item"
                  :deep="2"
                  highlight-mouseover
                  :showDoubleQuotes="true"
                  :showLength="true"
              ></vue-json-pretty>
            </v-card>
          </td>
        </template>

        <!-- État vide -->
        <template v-slot:[`no-data`]>
          <v-card-text class="text-center py-6">
            <v-icon size="64" color="grey lighten-1">mdi-database-off-outline</v-icon>
            <div class="text-h6 grey--text text--darken-1 mt-2">Aucune donnée disponible</div>
            <div class="text-body-2 grey--text">Aucun audit système n'a été trouvé</div>
          </v-card-text>
        </template>
      </v-data-table>
    </v-card>

    <!-- Snackbar pour les notifications -->
    <v-snackbar
        v-model="snackbar"
        :color="snackbarColor"
        timeout="3000"
        bottom
        right
    >
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn
            text
            v-bind="attrs"
            @click="snackbar = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import apiClient from "../../config/api.js";
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

function isMobileDevice() {
  return /Mobi|Android/i.test(navigator.userAgent);
}

export default {
  data() {
    return {
      isMobile: isMobileDevice(),
      search: "",
      loading: true,
      refreshing: false,
      serveraudits: [],
      filteredAudits: [],
      headers: [
        {text: "ID", value: "id", width: "60px"},
        {text: "Date début", value: "dateStart", width: "150px"},
        {text: "Date fin", value: "dateEnd", width: "150px"},
        {text: "URL de la requête", value: "requestURI", width: "200px"},
        {text: "Méthode", value: "method", width: "100px"},
        {text: "Utilisateur", value: "remoteUser", width: "150px"},
        {text: "Adresse IP", value: "remoteAddress", width: "150px"}
      ],
      expanded: [],
      singleExpand: true,
      itemsPerPage: 15,

      // Nouveaux champs pour les filtres
      dateMenu: false,
      dates: [],
      methodFilter: null,
      methodOptions: [
        {text: "GET", value: "GET"},
        {text: "POST", value: "POST"},
        {text: "PUT", value: "PUT"},
        {text: "DELETE", value: "DELETE"},
        {text: "PATCH", value: "PATCH"}
      ],

      // Notifications
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success'
    };
  },
  computed: {
    dateRangeText() {
      if (!this.dates || this.dates.length === 0) return 'Toutes les dates';
      if (this.dates.length === 1) return format(parseISO(this.dates[0]), 'dd/MM/yyyy');
      if (this.dates.length === 2) {
        return `${format(parseISO(this.dates[0]), 'dd/MM/yyyy')} - ${format(parseISO(this.dates[1]), 'dd/MM/yyyy')}`;
      }
      return 'Période sélectionnée';
    }
  },
  watch: {
    dates: {
      handler() {
        this.applyFilters();
      },
      deep: true
    },
    methodFilter() {
      this.applyFilters();
    }
  },
  created() {
    this.fetchData();
  },
  methods: {
    fetchData() {
      this.loading = true;
      apiClient
          .get(`${this.$store.getters.user.baseUrl}/admin/audits`)
          .then(response => {
            this.serveraudits = response.data.datas;
            this.filteredAudits = [...this.serveraudits];
            this.loading = false;
          })
          .catch(() => {
            this.loading = false;
            this.showSnackbar('Erreur lors du chargement des audits', 'error');
          });
    },
    refreshData() {
      this.refreshing = true;
      apiClient
          .get(`${this.$store.getters.user.baseUrl}/admin/audits`)
          .then(response => {
            this.serveraudits = response.data.datas;
            this.applyFilters();
            this.refreshing = false;
            this.showSnackbar('Audits actualisés avec succès', 'success');
          })
          .catch(() => {
            this.refreshing = false;
            this.showSnackbar('Erreur lors de l\'actualisation des audits', 'error');
          });
    },
    formatDateOnly(dateString) {
      if (!dateString) return 'N/A';
      try {
        return format(new Date(dateString), 'dd MMM yyyy', {locale: fr});
      } catch (e) {
        return dateString;
      }
    },
    formatTimeOnly(dateString) {
      if (!dateString) return '';
      try {
        return format(new Date(dateString), 'HH:mm:ss');
      } catch (e) {
        return '';
      }
    },
    expandRow(item, {isExpanded}) {
      if (isExpanded) return;
      this.expanded = [item];
    },
    downloadCSV() {
      const csvContent = this.convertToCSV(this.filteredAudits);
      const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `system-audits-${format(new Date(), 'yyyy-MM-dd')}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.showSnackbar('Fichier CSV téléchargé', 'success');
    },
    convertToCSV(data) {
      const headers = ['ID', 'Date début', 'Date fin', 'URL de la requête', 'Méthode', 'Utilisateur', 'Adresse IP'];
      let csvRows = [];

      csvRows.push(headers.join(','));

      for (const audit of data) {
        const row = [
          audit.id,
          audit.dateStart,
          audit.dateEnd,
          audit.requestURI,
          audit.method,
          audit.remoteUser || '',
          audit.remoteAddress
        ];

        csvRows.push(row.map(value => `"${value}"`).join(','));
      }

      return csvRows.join('\n');
    },
    applyFilters() {
      // Filtrer par méthode si sélectionnée
      let filtered = this.serveraudits;

      if (this.methodFilter) {
        filtered = filtered.filter(audit => audit.method === this.methodFilter);
      }

      // Filtrer par dates si sélectionnées
      if (this.dates && this.dates.length > 0) {
        if (this.dates.length === 1) {
          // Un seul jour sélectionné
          const selectedDate = new Date(this.dates[0]);
          selectedDate.setHours(0, 0, 0, 0);
          const nextDay = new Date(selectedDate);
          nextDay.setDate(nextDay.getDate() + 1);

          filtered = filtered.filter(audit => {
            const auditDate = new Date(audit.dateStart);
            return auditDate >= selectedDate && auditDate < nextDay;
          });
        } else if (this.dates.length === 2) {
          // Période sélectionnée
          const startDate = new Date(this.dates[0]);
          startDate.setHours(0, 0, 0, 0);
          const endDate = new Date(this.dates[1]);
          endDate.setHours(23, 59, 59, 999);

          filtered = filtered.filter(audit => {
            const auditDate = new Date(audit.dateStart);
            return auditDate >= startDate && auditDate <= endDate;
          });
        }
      }

      this.filteredAudits = filtered;
    },
    getMethodColor(method) {
      const colors = {
        GET: "primary",
        POST: "success",
        PUT: "warning",
        DELETE: "error",
        PATCH: "orange"
      };
      return colors[method] || "grey";
    },
    copyToClipboard(item) {
      try {
        navigator.clipboard.writeText(JSON.stringify(item, null, 2));
        this.showSnackbar('Copié dans le presse-papier', 'success');
      } catch (err) {
        this.showSnackbar('Erreur lors de la copie', 'error');
      }
    },
    showSnackbar(text, color = 'success') {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    dynamicID() {
      return 'dynamicID-' + Math.floor(Math.random() * Date.now().toString());
    }
  }
};
</script>

<style scoped>
.audit-header {
  background: linear-gradient(to right, #01579B, #1e88e5);
}

.expanded-details {
  border-radius: 4px;
  overflow: auto;
  max-height: 400px;
}

.audit-table ::v-deep th {
  font-weight: bold !important;
  background-color: #f5f5f5 !important;
}

.audit-table ::v-deep tbody tr {
  cursor: pointer;
  transition: background-color 0.2s;
}

.audit-table ::v-deep tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.03) !important;
}

.audit-table ::v-deep tbody tr:nth-of-type(even) {
  background-color: rgba(240, 240, 240, 0.5);
}

.audit-table ::v-deep tbody tr:nth-of-type(odd) {
  background-color: rgb(250, 250, 250);
}

.audit-table ::v-deep tbody tr.v-data-table__expanded__content {
  box-shadow: inset 0 4px 4px -4px rgba(0, 0, 0, 0.1);
}
</style>